import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { timeUntil } from "../../lib/format";
import { ACTIVE_PREVIEW, type Preview } from "../../types";
import { PreviewStatusBadge } from "../preview-status";
import { Button } from "../ui/button";
import { TableCell, TableRow } from "../ui/table";

export function PreviewRow({ preview, onOpen }: { preview: Preview; onOpen: () => void }) {
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["previews"] });
  const stop = useMutation({
    mutationFn: () => api.stopPreview(preview.id),
    onSuccess: () => {
      toast.success("Preview stopped");
      refresh();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Stop failed"),
  });
  const restart = useMutation({
    mutationFn: () => api.restartPreview(preview.id),
    onSuccess: () => {
      toast.success("Preview relaunching");
      refresh();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Retry failed"),
  });
  const active = ACTIVE_PREVIEW.has(preview.status);
  const sha = preview.requestedSha ?? preview.headSha;

  return (
    <TableRow className="cursor-pointer" onClick={onOpen}>
      <TableCell className="font-mono text-sm">{preview.slug}</TableCell>
      <TableCell>
        <PreviewStatusBadge preview={preview} />
      </TableCell>
      <TableCell>
        <a
          href={preview.prUrl}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-sm text-primary hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          #{preview.prNumber} <ArrowUpRight className="size-3" />
        </a>
      </TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">{sha?.slice(0, 7) ?? "—"}</TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {preview.status === "ready" && preview.expiresAt ? timeUntil(preview.expiresAt) : "—"}
      </TableCell>
      <TableCell onClick={(event) => event.stopPropagation()}>
        {preview.status === "ready" && preview.apps ? (
          <span className="flex gap-2 text-xs">
            {(["admin", "client", "employer"] as const).map((app) =>
              preview.apps?.[app] ? (
                <a
                  key={app}
                  href={preview.apps[app]}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  {app}
                </a>
              ) : null,
            )}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{preview.phase ?? "—"}</span>
        )}
      </TableCell>
      <TableCell onClick={(event) => event.stopPropagation()}>
        {active ? (
          <Button variant="ghost" size="sm" disabled={stop.isPending} onClick={() => stop.mutate()}>
            Stop
          </Button>
        ) : (
          <Button variant="ghost" size="sm" disabled={restart.isPending} onClick={() => restart.mutate()}>
            Retry
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
